import React, { useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FaFileCirclePlus, FaArrowRight } from 'react-icons/fa6';
import { ExpandHeader, ToolCallWrapper } from '../shared/ToolCallComponents';
import { FilePathDisplay } from '../../shared/FilePathDisplay';
import Text from '../../Text/Text.tsx';

interface FileToolProps {
    item: any;
    isInProgress: boolean;
}

export const FilePathText: React.FC<{text: string, type?: 'old' | 'new' | undefined, isDeleted?: boolean}> = ({ text, type, isDeleted }) => {
    const markdownText = `[${text}](${text})`;
    const textColor = type === 'old'
        ? "text-red-500 dark:text-red-400"
        : type === 'new'
            ? "text-green-500 dark:text-green-400"
            : "";

    const deletedStyles = isDeleted
        ? "cursor-not-allowed opacity-50 pointer-events-none select-none bg-neutral-200 dark:bg-neutral-800 px-1 rounded"
        : "cursor-pointer hover:underline";

    return (
        <div className={`${textColor} ${deletedStyles}`}>
            <Text item={{ text: markdownText }} index={0} />
        </div>
    );
};

const FileOperationContent: React.FC<{item: any}> = ({ item }) => {
    const isMoveOperation = item.name === "moveFile";
    const isDeleteOperation = item.name === "deleteFile";
    const isCreateOperation = item.name === "createFile";

    return (
        <div className="ml-2">
            {item.args?.description && (
                <div className="mb-2 text-neutral-500 dark:text-neutral-400">
                    {item.args.description}
                </div>
            )}
            <div className="space-y-1">
                <FilePathDisplay
                    filePath={item.args?.filePath || ''}
                    newFilePath={item.args?.newFilePath}
                    operation={isMoveOperation ? 'move' : isDeleteOperation ? 'delete' : isCreateOperation ? 'create' : 'edit'}
                    originalFilePath={item.args?.filePath}
                />
            </div>
            {(item.args?.newContents || item.deletedContents) && (
                <div className="pl-4 pr-2 py-2 mt-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-black overflow-x-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                        {item.deletedContents && (
                            <div className="mb-5 space-y-1">
                                <div className="text-red-500 dark:text-red-400">{'---'}</div>
                                {item.deletedContents.split('\n').map((line: string, index: number) => (
                                    <div key={`deleted-${index}`}
                                         className="text-red-500 dark:text-red-400">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        )}
                        {item.args?.newContents && (
                            <div className="mt-5 space-y-1">
                                <div className="text-green-600 dark:text-green-400">{'+++'}</div>
                                {item.args.newContents.split('\n').map((line: string, index: number) => (
                                    <div key={`added-${index}`}
                                         className="text-green-600 dark:text-green-400">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        )}
                    </pre>
                </div>
            )}
        </div>
    );
};

const FileToolBase: React.FC<FileToolProps & {title: string, icon: React.ReactNode}> = ({
    item,
    isInProgress,
    title,
    icon
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <ToolCallWrapper>
            <div className="flex items-center justify-between w-full">
                <ExpandHeader
                    icon={icon}
                    title={title}
                    isExpanded={isExpanded}
                    isInProgress={isInProgress}
                    onClick={() => setIsExpanded(prev => !prev)}
                />
            </div>
            {isExpanded && <FileOperationContent item={item} />}
        </ToolCallWrapper>
    );
};

export const EditFile: React.FC<FileToolProps> = (props) => (
    <FileToolBase
        {...props}
        title="Editing file"
        icon={<FaEdit className="mr-2 text-neutral-500 dark:text-neutral-400" size={14}/>}
    />
);

export const CreateFile: React.FC<FileToolProps> = (props) => (
    <FileToolBase
        {...props}
        title="Creating file"
        icon={<FaFileCirclePlus className="mr-2 text-neutral-500 dark:text-neutral-400" size={14}/>}
    />
);

export const DeleteFile: React.FC<FileToolProps> = (props) => (
    <FileToolBase
        {...props}
        title="Deleting file"
        icon={<FaTrash className="mr-2 text-neutral-500 dark:text-neutral-400" size={14}/>}
    />
);

export const MoveFile: React.FC<FileToolProps> = (props) => (
    <FileToolBase
        {...props}
        title="Moving file"
        icon={<FaArrowRight className="mr-2 text-neutral-500 dark:text-neutral-400" size={14}/>}
    />
);
